import { useState } from 'react'
import { analyzePrompt, PromptAnalysis } from '../services/llm'
import { SeniorButton } from './SeniorButton'
import { SeniorInput } from './SeniorInput'
import { useAccessibility } from '../contexts/AccessibilityContext'

interface ExplainModalProps {
  isOpen: boolean
  title: string
  content: string
  onClose: () => void
}

const HelpButton = ({ onClick }: { onClick: () => void }) => {
  const { settings } = useAccessibility()

  const getSize = () => {
    switch (settings.fontSize) {
      case 'small':
        return 'w-8 h-8 text-sm'
      case 'medium':
        return 'w-10 h-10 text-base'
      case 'large':
        return 'w-12 h-12 text-lg'
      case 'extra-large':
        return 'w-14 h-14 text-xl'
    }
  }

  const getColors = () => {
    return settings.contrast === 'high'
      ? 'bg-black text-white hover:bg-gray-800 border-2 border-black'
      : 'bg-blue-100 text-blue-600 hover:bg-blue-200 border-2 border-blue-100'
  }

  return (
    <button
      onClick={onClick}
      className={`ml-3 ${getSize()} ${getColors()} rounded-full flex items-center justify-center font-bold transition-colors focus:outline-none focus:ring-4 focus:ring-blue-300`}
      title="Объяснить"
      aria-label="Получить объяснение"
    >
      ?
    </button>
  )
}

const ExplainModal = ({
  isOpen,
  title,
  content,
  onClose,
}: ExplainModalProps) => {
  const { settings } = useAccessibility()

  if (!isOpen) return null

  const getTitleSize = () => {
    switch (settings.fontSize) {
      case 'small':
        return 'text-xl'
      case 'medium':
        return 'text-2xl'
      case 'large':
        return 'text-3xl'
      case 'extra-large':
        return 'text-4xl'
    }
  }

  const getContentSize = () => {
    switch (settings.fontSize) {
      case 'small':
        return 'text-lg'
      case 'medium':
        return 'text-xl'
      case 'large':
        return 'text-2xl'
      case 'extra-large':
        return 'text-3xl'
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3
          id="modal-title"
          className={`${getTitleSize()} font-semibold text-gray-900 mb-6`}
        >
          {title}
        </h3>
        <p className={`${getContentSize()} text-gray-600 mb-8 leading-relaxed`}>
          {content}
        </p>
        <SeniorButton
          onClick={onClose}
          variant="primary"
          size="large"
          className="w-full"
          autoFocus
        >
          Понятно
        </SeniorButton>
      </div>
    </div>
  )
}

const HELP_CONTENT = {
  score: {
    title: 'Как работает оценка?',
    content:
      'Оценка от 1 до 10 показывает, насколько понятно вы сформулировали свою просьбу. Как в школе: 10 - отлично, 5 - удовлетворительно. Чем выше оценка, тем лучше машина поймет, что вы хотите.',
  },
  analogy: {
    title: 'Зачем нужны сравнения?',
    content:
      'Сравнения помогают понять новое через знакомое. Как объяснить работу компьютера через пример с библиотекарем - и то, и другое помогает найти нужную информацию.',
  },
  suggestions: {
    title: 'Как использовать советы?',
    content:
      'Советы показывают, как сделать вашу просьбу понятнее. Следуйте им по порядку - и машина будет давать более точные ответы.',
  },
  analysis: {
    title: 'Что означают цветные точки?',
    content:
      'Зеленые точки показывают, что в вашей просьбе хорошо, красные - что можно улучшить. Как светофор: зеленый - можно идти дальше, красный - стоит подумать еще.',
  },
}

export const PromptCoach = () => {
  const { settings } = useAccessibility()
  const [prompt, setPrompt] = useState('')
  const [feedback, setFeedback] = useState<PromptAnalysis | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [explainModal, setExplainModal] = useState<{
    isOpen: boolean
    key: keyof typeof HELP_CONTENT
  }>({
    isOpen: false,
    key: 'score',
  })

  const openExplain = (key: keyof typeof HELP_CONTENT) => {
    setExplainModal({ isOpen: true, key })
  }

  const closeExplain = () => {
    setExplainModal({ isOpen: false, key: 'score' })
  }

  const handleAnalyze = async (e?: React.MouseEvent | React.FormEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    setIsAnalyzing(true)
    setError(null)

    try {
      const analysis = await analyzePrompt(prompt)
      console.log('got response analysis')

      setFeedback(analysis)
    } catch (err) {
      console.log('error')
      setError(
        err instanceof Error ? err.message : 'Произошла ошибка при анализе'
      )
    } finally {
      console.log('finally')

      setIsAnalyzing(false)
    }
  }

  const getTitleSize = () => {
    switch (settings.fontSize) {
      case 'small':
        return 'text-2xl'
      case 'medium':
        return 'text-3xl'
      case 'large':
        return 'text-4xl'
      case 'extra-large':
        return 'text-5xl'
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-8 bg-white rounded-lg shadow-lg">
      <h2
        className={`${getTitleSize()} font-bold text-gray-900 mb-8 text-center`}
      >
        Потренируемся говорить с машиной
      </h2>

      <form
        className="space-y-8"
        onSubmit={e => {
          e.preventDefault()
          e.stopPropagation()
          handleAnalyze(e)
          return false
        }}
      >
        <SeniorInput
          label="Напишите вашу просьбу машине:"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault()
              handleAnalyze()
            }
          }}
          placeholder="Например: Расскажи мне про погоду сегодня..."
          helpText="Чем понятнее вы объясните, что хотите, тем лучше машина поможет"
          error={error || undefined}
          type="textarea"
          icon="✏️"
        />

        <div className="text-center">
          <SeniorButton
            onClick={handleAnalyze}
            disabled={!prompt.trim() || isAnalyzing}
            variant="primary"
            size="large"
            icon={isAnalyzing ? '⏳' : '🔍'}
          >
            {isAnalyzing ? 'Проверяю...' : 'Проверить мою просьбу'}
          </SeniorButton>
        </div>

        {feedback && (
          <div className="mt-8 p-8 bg-blue-50 rounded-lg border-2 border-blue-200">
            <h3
              className={`${getTitleSize()} font-semibold text-gray-900 mb-6`}
            >
              Результат проверки
            </h3>

            <div className="space-y-6">
              <div>
                <div className="flex items-center mb-4">
                  <p className="text-2xl font-medium text-gray-700">
                    Оценка: {feedback.score} из 10
                  </p>
                  <HelpButton onClick={() => openExplain('score')} />
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6">
                  <div
                    className={`h-6 rounded-full transition-all ${
                      feedback.score >= 8
                        ? 'bg-green-500'
                        : feedback.score >= 6
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                    }`}
                    style={{ width: `${(feedback.score / 10) * 100}%` }}
                  />
                </div>
                <p className="text-lg text-gray-600 mt-2">
                  {feedback.score >= 8
                    ? '🎉 Отлично!'
                    : feedback.score >= 6
                      ? '👍 Хорошо, но можно лучше'
                      : '💡 Есть что улучшить'}
                </p>
              </div>

              <div>
                <p className="text-xl font-medium text-gray-700 mb-3">
                  Объяснение:
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  {feedback.explanation}
                </p>
              </div>

              <div>
                <div className="flex items-center mb-3">
                  <p className="text-xl font-medium text-gray-700">
                    Сравнение:
                  </p>
                  <HelpButton onClick={() => openExplain('analogy')} />
                </div>
                <p className="text-lg text-gray-600 italic leading-relaxed bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  💡 {feedback.analogy}
                </p>
              </div>

              <div>
                <div className="flex items-center mb-3">
                  <p className="text-xl font-medium text-gray-700">
                    Советы по улучшению:
                  </p>
                  <HelpButton onClick={() => openExplain('suggestions')} />
                </div>
                <ul className="space-y-3 text-lg text-gray-600">
                  {feedback.suggestions.map((suggestion, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200"
                    >
                      <span className="text-blue-500 font-bold text-xl">
                        {index + 1}.
                      </span>
                      <span className="leading-relaxed">{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {feedback.ruleAnalysis && (
                <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center mb-4">
                    <p className="text-xl font-medium text-gray-700">
                      Подробная проверка:
                    </p>
                    <HelpButton onClick={() => openExplain('analysis')} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${feedback.ruleAnalysis.hasPoliteLanguage ? 'bg-green-500' : 'bg-red-500'}`}
                      >
                        {feedback.ruleAnalysis.hasPoliteLanguage ? '✓' : '✗'}
                      </span>
                      <span className="text-lg">Вежливо сказано</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${feedback.ruleAnalysis.hasContext ? 'bg-green-500' : 'bg-red-500'}`}
                      >
                        {feedback.ruleAnalysis.hasContext ? '✓' : '✗'}
                      </span>
                      <span className="text-lg">Дан контекст</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${feedback.ruleAnalysis.hasSpecificRequest ? 'bg-green-500' : 'bg-red-500'}`}
                      >
                        {feedback.ruleAnalysis.hasSpecificRequest ? '✓' : '✗'}
                      </span>
                      <span className="text-lg">Четкая просьба</span>
                    </div>
                    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg">
                      <span
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-sm font-bold ${!feedback.ruleAnalysis.isTooShort ? 'bg-green-500' : 'bg-red-500'}`}
                      >
                        {!feedback.ruleAnalysis.isTooShort ? '✓' : '✗'}
                      </span>
                      <span className="text-lg">Достаточно подробно</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </form>

      <ExplainModal
        isOpen={explainModal.isOpen}
        title={HELP_CONTENT[explainModal.key].title}
        content={HELP_CONTENT[explainModal.key].content}
        onClose={closeExplain}
      />
    </div>
  )
}
